import { useEffect, useRef, useState, useCallback } from 'react';
import { useConversationSearch } from '../hooks/useConversationSearch';
import { useResponsive } from '../hooks/useResponsive';
import { useThemeTemplates } from '../hooks/useThemeTemplates';
import SkeletonSummary from './skeleton/skeletonSummary';
import NotesSkeletonLoader from './skeleton/notesSkeletonLoader';
import Results from './results/results';
import WithoutResults from './withoutResults/withoutResults';
import SearchForm from './searchForm/searchForm';
import Trending from './trending/trending';
import ThemeHeader from './header/ThemeHeader';
import ThemeFooter from './footer/ThemeFooter';
import ScriptInjector from './scriptInjector/ScriptInjector';
import ThemeStyle from './style/ThemeStyle';
import './skeleton/skeletonLoader.sass';
import { useMenuOpen } from '../hooks/useMenuOpen';

// Valor de ?q= con el que el sitio abre el buscador limpio («Nueva conversación»).
const NEW_CONVERSATION = 'new-conversation';

export default function BuscadorApp() {
  const { isMobile } = useResponsive();
  const { theme } = useThemeTemplates();
  const [query, setQuery] = useState('');
  const isMenuOpen = useMenuOpen();
  const [searchParamsChanged, setSearchParamsChanged] = useState(false);
  // Cómo llegó la búsqueda (escrita, sugerencia o url): va al tracking del agente.
  const origenRef = useRef('escrita');
  const [headerReady, setHeaderReady] = useState(false);
  const [footerReady, setFooterReady] = useState(false);
  const handleHeaderReady = useCallback(() => setHeaderReady(true), []);
  const handleFooterReady = useCallback(() => setFooterReady(true), []);
  const {
    conversation,
    showLoading,
    isSearching,
    storageWasEmpty,
    search,
    clearConversation,
    startNewConversation,
  } = useConversationSearch();

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    setQuery('');

    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    // replaceState: con pushState, ?q=new-conversation quedaba en el historial y al dar
    // «Atrás» se buscaba como texto.
    window.history.replaceState({}, '', url.toString());
  }, [startNewConversation]);

  // Sincroniza la primera búsqueda compartida mediante ?q=.
  useEffect(() => {
    if (!query) {
      const urlParams = new URLSearchParams(window.location.search);
      const initialQuery = urlParams.get('q') || '';

      // Caso especial: ?q=new-conversation borra el historial al cargar,
      // igual que el botón "Nueva conversación".
      if (initialQuery === NEW_CONVERSATION) {
        handleNewConversation();
        return;
      }

      if (!initialQuery.trim()) return;

      origenRef.current = 'url';
      setQuery(initialQuery);
      setSearchParamsChanged(true);
    }
  }, [query, handleNewConversation]);

  // Sincroniza las acciones atrás/adelante con la búsqueda y el historial visible.
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const nextQuery = urlParams.get('q');

      if (!nextQuery) {
        setQuery('');
        clearConversation();
        return;
      }

      if (nextQuery === NEW_CONVERSATION) {
        handleNewConversation();
        return;
      }

      origenRef.current = 'url';
      setQuery(nextQuery);
      setSearchParamsChanged(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [clearConversation, handleNewConversation]);

  useEffect(() => {
    if (!searchParamsChanged) return;

    setSearchParamsChanged(false);
    search(query, { origen: origenRef.current });

    // Sin el ?q= en la URL, recargar la página no repite la búsqueda.
    const url = new URL(window.location.href);
    if (url.searchParams.has('q')) {
      url.searchParams.delete('q');
      window.history.replaceState({}, '', url.toString());
    }
  }, [query, search, searchParamsChanged]);

  // Escuchar clic en .social-item.cuno del theme (event delegation desde React)
  useEffect(() => {
    const handleSocialCunoClick = (e) => {
      const target = e.target.closest('.social-share-float .social-item.cuno');
      if (target) {
        handleNewConversation();
      }
    };
    document.addEventListener('click', handleSocialCunoClick);
    return () => document.removeEventListener('click', handleSocialCunoClick);
  }, [handleNewConversation]);

  const handleSearchSubmit = useCallback((nextQuery, origen = 'escrita') => {
    const trimmedQuery = nextQuery.trim();
    if (!trimmedQuery || isSearching) return;

    origenRef.current = origen;
    setQuery(trimmedQuery);
    setSearchParamsChanged(true);
  }, [isSearching]);

  const showFullSkeleton = (
    showLoading
    && query
    && conversation.length === 0
    && storageWasEmpty
  );
  const hasConversation = conversation.length > 0;
  const isSearchActive = hasConversation || showFullSkeleton;

  return (
    <>
      <ThemeStyle css={theme.css} />
      {isMenuOpen && <div id={`body-overlay`}></div>}
      <ThemeHeader
        header={theme.header}
        onReady={handleHeaderReady}
      />

      <main className={`grid-layout${isSearchActive ? ' has-results' : ''}`}>
        <div className='container-cuno-search'>
          {!isSearchActive &&
            <>
              <div className='container-user-search'>
                <h1>Hola, ¿Cómo puedo ayudarte?</h1>
              </div>

              <SearchForm
                onSubmit={handleSearchSubmit}
                isSearching={isSearching}
                isSearchActive={isSearchActive}
              />
            </>
          }

          {showFullSkeleton && (
            <div className="results-container">
              <div className="container-full-results">
                <div className="conversation-row">
                  <SkeletonSummary />
                  <NotesSkeletonLoader
                    isMobile={isMobile}
                  />
                </div>
              </div>
            </div>
          )}

          {!showFullSkeleton && hasConversation && (
            <Results
              conversation={conversation}
              isMobile={isMobile}
              onNewConversation={handleNewConversation}
            />
          )}

          {!showFullSkeleton && !hasConversation && !query && (
            <Trending
              onSearch={handleSearchSubmit}
              isSearching={isSearching}
            />
          )}
        </div>
      </main>

      {isSearchActive && <SearchForm
        onSubmit={handleSearchSubmit}
        isSearching={isSearching}
        isSearchActive={isSearchActive}
      />
      }

      <ThemeFooter
        footer={theme.footer}
        onReady={handleFooterReady}
      />
      {headerReady && footerReady && <ScriptInjector script={theme.script} />}
    </>
  );
}
